import { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "../../lib/api";
import { masterUrl, timeAgo, copyToClipboard } from "../../lib/inviteUi";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Badge } from "../ui/badge";
import { Plus, Copy, Check, ChevronRight, MessageCircle, ExternalLink, Megaphone } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin — MASTER invites: one reusable link per product, built for message
 * blasting (WhatsApp/SMS). The admin appends a free-form variable per
 * recipient — usually their mobile number or an internal reference id:
 *
 *   offer.express/m/<slug>/9876543210      (path style)
 *   offer.express/m/<slug>?v=CUST-1042     (query style)
 *
 * Every variable value gets its own funnel row (opened → phone typed →
 * submitted → redirected) so blasting ONE link still shows per-recipient
 * activity. Rows here are the master links; the drawer drills into the
 * per-variable rows fetched from GET /api/admin/invites/{slug}/tags.
 */
export default function MasterInvitesSection({ invites, products, onChanged }) {
  const [createOpen, setCreateOpen] = useState(false);
  const [successInvite, setSuccessInvite] = useState(null);
  const [detail, setDetail] = useState(null);

  // Aggregate across all master links for the header strip.
  const totals = useMemo(() => {
    const t = { links: invites.length, variables: 0, opened: 0, captured: 0, redirected: 0 };
    for (const inv of invites) {
      const s = inv.tag_stats || {};
      t.variables += s.variables || 0;
      t.opened += s.opened || 0;
      t.captured += s.captured || 0;
      t.redirected += s.redirected || 0;
    }
    return t;
  }, [invites]);

  return (
    <div data-testid="admin-master-invites-section">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
        <div className="text-[13px] text-neutral-600 flex flex-wrap items-center gap-x-3 gap-y-1 pt-1">
          <span><b className="text-black">{totals.links}</b> master links</span>
          <span className="text-neutral-300">·</span>
          <span><b className="text-black">{totals.variables}</b> variables seen</span>
          <span className="text-neutral-300">·</span>
          <span><b className="text-black">{totals.opened}</b> opened</span>
          <span className="text-neutral-300">·</span>
          <span><b className="text-black">{totals.captured}</b> phone submitted</span>
          <span className="text-neutral-300">·</span>
          <span><b className="text-black">{totals.redirected}</b> redirected</span>
        </div>
        <Button
          data-testid="new-master-btn"
          onClick={() => setCreateOpen(true)}
          className="bg-black text-white hover:bg-[#0055FF]"
        >
          <Megaphone className="w-4 h-4 mr-1.5" /> New master link
        </Button>
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Created</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Base link</TableHead>
              <TableHead>Variables</TableHead>
              <TableHead>Opened</TableHead>
              <TableHead>Submitted</TableHead>
              <TableHead>Redirected</TableHead>
              <TableHead className="w-[80px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invites.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center py-10 text-sm text-neutral-500">
                No master links yet. Click <b>New master link</b> to create one for blasting.
              </TableCell></TableRow>
            ) : invites.map((inv) => (
              <MasterRow key={inv.slug} inv={inv} onOpen={() => setDetail(inv)} />
            ))}
          </TableBody>
        </Table>
      </div>

      <CreateMasterDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        products={products}
        onCreated={(created) => {
          setCreateOpen(false);
          setSuccessInvite(created);
          onChanged?.();
        }}
      />

      <MasterSuccessDialog invite={successInvite} onClose={() => setSuccessInvite(null)} />
      <MasterDetailDialog invite={detail} onClose={() => setDetail(null)} />
    </div>
  );
}

// ============================================================================
// Row
// ============================================================================
function MasterRow({ inv, onOpen }) {
  const url = masterUrl(inv.slug);
  const s = inv.tag_stats || {};
  return (
    <TableRow
      data-testid={`master-row-${inv.slug}`}
      className="cursor-pointer hover:bg-neutral-50"
      onClick={onOpen}
    >
      <TableCell>
        <div className="font-medium">{timeAgo(inv.created_at)}</div>
        <div className="text-[11px] text-neutral-500">{inv.created_at?.slice(0, 10)}</div>
      </TableCell>
      <TableCell>
        {inv.product_title ? (
          <div className="font-medium">{inv.product_provider || inv.product_title}</div>
        ) : (
          <div className="font-medium">Any</div>
        )}
        {inv.allowed_tags?.length > 0 && (
          <Badge variant="outline" className="mt-1 text-[10px]">{inv.allowed_tags.length} allowed variables</Badge>
        )}
      </TableCell>
      <TableCell>
        <code className="font-mono text-[11px] bg-neutral-50 border border-neutral-200 rounded px-2 py-1 block max-w-[240px] truncate" title={url}>
          {url.replace(/^https?:\/\//, "")}
        </code>
      </TableCell>
      <TableCell>
        <Badge className="bg-[#f0f4ff] text-[#0055FF] hover:bg-[#f0f4ff] border-[#e0e7ff]">{s.variables || 0}</Badge>
      </TableCell>
      <TableCell>{s.opened || 0}</TableCell>
      <TableCell>{s.captured || 0}</TableCell>
      <TableCell>{s.redirected || 0}</TableCell>
      <TableCell>
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            title="Copy base link"
            className="w-8 h-8 grid place-items-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100 hover:text-black"
            onClick={() => copyToClipboard(url)}
            data-testid={`master-copy-${inv.slug}`}
          ><Copy className="w-3.5 h-3.5" /></button>
          <button
            title="View per-variable activity"
            className="w-8 h-8 grid place-items-center rounded-md border border-neutral-200 text-neutral-500 hover:bg-neutral-100 hover:text-black"
            onClick={onOpen}
            data-testid={`master-detail-${inv.slug}`}
          ><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// Variable composer — type a variable, get both link formats + share actions.
// Used by the success dialog and the detail drawer.
// ============================================================================
export function VariableComposer({ slug }) {
  const [tag, setTag] = useState("");
  const base = masterUrl(slug);
  const pathLink = masterUrl(slug, tag, "path");
  const queryLink = masterUrl(slug, tag, "query");

  return (
    <div className="space-y-3">
      <div>
        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Base link (untagged)</Label>
        <div className="flex items-stretch border border-neutral-200 rounded-lg overflow-hidden">
          <code className="flex-1 px-3 py-2.5 font-mono text-[13px] bg-neutral-50 truncate" title={base}>{base}</code>
          <button
            type="button"
            className="border-l border-neutral-200 bg-white hover:bg-neutral-50 px-3 flex items-center gap-1 text-sm font-medium"
            onClick={() => copyToClipboard(base)}
            data-testid="master-base-copy"
          ><Copy className="w-3.5 h-3.5" /> Copy</button>
        </div>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">
          Add variable — mobile number or your reference id
        </Label>
        <Input
          data-testid="master-var-input"
          placeholder="e.g. 9876543210 or CUST-1042"
          value={tag}
          onChange={(e) => setTag(e.target.value)}
        />
        <p className="text-[11px] text-neutral-500 mt-1">
          Each unique value gets its own tracked row — open, phone typed, submitted, redirected.
        </p>
      </div>

      {tag.trim() && (
        <div className="space-y-2">
          <div>
            <div className="text-[11px] font-semibold text-neutral-500 mb-1">Path style</div>
            <div className="flex items-stretch border border-neutral-200 rounded-lg overflow-hidden">
              <code className="flex-1 px-3 py-2 font-mono text-[12px] bg-neutral-50 truncate" data-testid="master-path-link" title={pathLink}>{pathLink}</code>
              <button
                type="button"
                className="border-l border-neutral-200 bg-white hover:bg-neutral-50 px-3 text-sm font-medium"
                onClick={() => copyToClipboard(pathLink)}
              ><Copy className="w-3.5 h-3.5 inline mr-1" />Copy</button>
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-neutral-500 mb-1">Query style</div>
            <div className="flex items-stretch border border-neutral-200 rounded-lg overflow-hidden">
              <code className="flex-1 px-3 py-2 font-mono text-[12px] bg-neutral-50 truncate" data-testid="master-query-link" title={queryLink}>{queryLink}</code>
              <button
                type="button"
                className="border-l border-neutral-200 bg-white hover:bg-neutral-50 px-3 text-sm font-medium"
                onClick={() => copyToClipboard(queryLink)}
              ><Copy className="w-3.5 h-3.5 inline mr-1" />Copy</button>
            </div>
          </div>
          <a
            href={`https://wa.me/?text=${encodeURIComponent(`Here's your application link — ${pathLink}`)}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-700 hover:underline"
            data-testid="master-whatsapp-share"
          ><MessageCircle className="w-4 h-4" /> Share on WhatsApp</a>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Create dialog
// ============================================================================
function CreateMasterDialog({ open, onOpenChange, products, onCreated }) {
  const [productId, setProductId] = useState("");
  const [whitelist, setWhitelist] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setProductId(""); setWhitelist(""); setBusy(false);
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const allowed = whitelist
        .split(/[\n,]+/)
        .map((t) => t.trim())
        .filter(Boolean);
      const { data } = await api.post("/admin/invites", {
        invite_type: "master",
        product_id: productId || undefined,
        allowed_tags: allowed.length ? allowed : undefined,
      });
      toast.success("Master link created");
      onCreated?.(data);
    } catch (err) {
      toast.error(apiErrorMessage(err) || "Could not create master link");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="new-master-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Create master link</DialogTitle>
          <p className="text-sm text-neutral-600">
            One link for blasting. Append a variable per recipient (mobile number or reference id) — each gets tracked separately.
          </p>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Product</Label>
            <Select value={productId || "any"} onValueChange={(v) => setProductId(v === "any" ? "" : v)}>
              <SelectTrigger data-testid="master-product"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Any product — customer picks on landing</SelectItem>
                {(() => {
                  const banking = products.filter((p) => p.type === "banking");
                  const ecom    = products.filter((p) => p.type === "ecom");
                  const other   = products.filter((p) => p.type !== "banking" && p.type !== "ecom");
                  const group = (label, arr) => arr.length ? (
                    <div key={label}>
                      <div className="px-2 pt-2 pb-1 text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-semibold">{label}</div>
                      {arr.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>
                      ))}
                    </div>
                  ) : null;
                  return [
                    group("Banking / Loans", banking),
                    group("E-commerce / Cashback", ecom),
                    group("Other", other),
                  ];
                })()}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-neutral-500">
              Allowed variables (optional)
            </Label>
            <textarea
              data-testid="master-whitelist"
              className="w-full min-h-[88px] rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder={"One per line, e.g.\n9876543210\nCUST-1042"}
              value={whitelist}
              onChange={(e) => setWhitelist(e.target.value)}
            />
            <p className="text-[11px] text-neutral-500 mt-1">
              Leave empty to accept any variable. If set, links with unknown variables show an invalid-link page and are not tracked.
            </p>
          </div>
          <Button
            type="submit"
            data-testid="master-submit"
            disabled={busy}
            className="w-full h-11 bg-[#0055FF] text-white hover:opacity-90"
          >
            {busy ? "Creating…" : (<>Generate master link <ChevronRight className="w-4 h-4 ml-1" /></>)}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Success dialog — base link + variable composer
// ============================================================================
function MasterSuccessDialog({ invite, onClose }) {
  if (!invite) return null;
  return (
    <Dialog open={!!invite} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="master-success-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-emerald-50 grid place-items-center mb-2">
            <Check className="w-6 h-6 text-emerald-600" />
          </div>
          <DialogTitle className="font-display text-2xl tracking-tight">Master link ready</DialogTitle>
          <p className="text-sm text-neutral-600">
            {invite.product_title || "Any product"} · append a variable per recipient before blasting
          </p>
        </DialogHeader>
        <div className="mt-2">
          <VariableComposer slug={invite.slug} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Detail drawer — per-variable activity table
// ============================================================================
function MasterDetailDialog({ invite, onClose }) {
  const [tags, setTags] = useState(null);

  useEffect(() => {
    if (!invite) { setTags(null); return; }
    setTags(null);
    api.get(`/admin/invites/${invite.slug}/tags`)
      .then(({ data }) => setTags(data?.tags || []))
      .catch(() => {
        setTags([]);
        toast.error("Could not load variable activity");
      });
  }, [invite]);

  if (!invite) return null;
  const base = masterUrl(invite.slug);

  return (
    <Dialog open={!!invite} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="master-detail-dialog">
        <DialogHeader>
          <Badge variant="outline" className="font-mono text-[11px] w-fit mb-2">{invite.slug}</Badge>
          <DialogTitle className="font-display text-2xl tracking-tight">
            {invite.product_title || "Any product"} — master link
          </DialogTitle>
          <p className="text-sm text-neutral-600 flex items-center gap-2 flex-wrap">
            <code className="font-mono text-[11px] bg-neutral-100 px-1.5 py-0.5 rounded">{base}</code>
            <button className="text-xs font-medium underline underline-offset-2" onClick={() => copyToClipboard(base)}>Copy</button>
            {invite.allowed_tags?.length > 0 && (
              <Badge variant="outline" className="text-[10px]">whitelist: {invite.allowed_tags.length} variables</Badge>
            )}
          </p>
        </DialogHeader>

        <div className="rounded-xl border border-neutral-200 overflow-hidden mt-2">
          <Table data-testid="master-tag-table">
            <TableHeader>
              <TableRow>
                <TableHead>Variable</TableHead>
                <TableHead>Opened</TableHead>
                <TableHead>Phone: typed → submitted</TableHead>
                <TableHead>Redirected to</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tags === null ? (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-sm text-neutral-500">Loading…</TableCell></TableRow>
              ) : tags.length === 0 ? (
                <TableRow><TableCell colSpan={4} className="text-center py-10 text-sm text-neutral-500">
                  No activity yet. Blast the link with a variable per recipient and rows appear here.
                </TableCell></TableRow>
              ) : tags.map((t) => (
                <TableRow key={t.id} data-testid={`master-tag-row-${t.tag || "untagged"}`}>
                  <TableCell>
                    <span className="font-mono text-[12px] bg-neutral-100 px-1.5 py-0.5 rounded">
                      {t.tag || <span className="text-neutral-400">(no variable)</span>}
                    </span>
                    <div className="text-[11px] text-neutral-500 mt-0.5">first seen {timeAgo(t.created_at)}</div>
                  </TableCell>
                  <TableCell>
                    {t.opened_at ? (
                      <>
                        <Badge className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50 border-emerald-200">✓ {timeAgo(t.opened_at)}</Badge>
                        <div className="text-[11px] text-neutral-500 mt-0.5">{t.open_count} open{t.open_count === 1 ? "" : "s"}</div>
                      </>
                    ) : <Badge variant="outline" className="text-neutral-500">Not opened</Badge>}
                  </TableCell>
                  <TableCell>
                    {(t.typed_phone || t.captured_phone) ? (
                      <>
                        <div className="font-mono text-[12px] flex items-center gap-1.5">
                          <span>{t.typed_phone || <span className="text-neutral-300">—</span>}</span>
                          <span className="text-neutral-300">→</span>
                          <span>{t.captured_phone || <span className="text-neutral-300">—</span>}</span>
                          {t.phone_matched === true && <span className="w-2 h-2 rounded-full bg-emerald-500 ml-1" title="submitted number matches the variable" />}
                          {t.phone_matched === false && <span className="w-2 h-2 rounded-full bg-red-500 ml-1" title="submitted a different number" />}
                        </div>
                        <div className="text-[11px] text-neutral-500 mt-0.5">
                          {t.captured_phone
                            ? (t.phone_matched === true
                                ? "Submitted — matches variable ✓"
                                : t.phone_matched === false
                                  ? "Submitted — different number"
                                  : "Submitted")
                            : <Badge variant="outline" className="text-amber-700 border-amber-200 bg-amber-50 text-[10px]">Abandoned before submit</Badge>}
                        </div>
                      </>
                    ) : <span className="text-neutral-400 text-sm">—</span>}
                  </TableCell>
                  <TableCell>
                    {t.final_redirect_url ? (
                      <a
                        href={t.final_redirect_url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-[11px] text-sky-700 hover:underline inline-flex items-center gap-1 max-w-[220px] truncate"
                        title={t.final_redirect_url}
                      >
                        <ExternalLink className="w-3 h-3 shrink-0" />
                        {t.final_redirect_url.replace(/^https?:\/\//, "").slice(0, 40)}…
                      </a>
                    ) : <span className="text-neutral-400 text-sm">—</span>}
                    {t.final_click_id && (
                      <div className="font-mono text-[10px] text-neutral-500 mt-0.5">{t.final_click_id}</div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        <div className="mt-4 rounded-xl border border-neutral-200 p-4 bg-neutral-50/50">
          <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-3">Compose a tracked link</div>
          <VariableComposer slug={invite.slug} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
